import React, { useState, useEffect, useCallback } from 'react';
import './Header.css';
import { FaSearch, FaTimes, FaInfoCircle } from 'react-icons/fa';
import { MdLightMode, MdDarkMode } from 'react-icons/md';
import nlp from 'compromise';

const Header = ({
    onSearch,
    setIsDisclaimerPopupOpen,
    fetchTrendingData,
    news,
    setIsPopupOpen,
    setCurrentTime,
    searchTerm,
    setSearchTerm
}) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isSearchFocused, setIsSearchFocused] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(
        window.matchMedia('(prefers-color-scheme: dark)').matches
    );
    const [entities, setEntities] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 10);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleSystemThemeChange = (e) => {
            setIsDarkMode(e.matches);
            document.body.classList.toggle('dark-mode', e.matches);
        };
        document.body.classList.toggle('dark-mode', isDarkMode);
        mediaQuery.addEventListener('change', handleSystemThemeChange);
        return () => mediaQuery.removeEventListener('change', handleSystemThemeChange);
    }, [isDarkMode]);

    const adjustType = (text, type) => {
        const lowerText = text.toLowerCase();

        // Event keywords expanded to cover more common Indonesian events and news topics
        const eventKeywords = [
            // Natural disasters
            "banjir", "gempa", "tsunami", "longsor", "gunung meletus", "erupsi", "kekeringan",
            // Religious/cultural events
            "ramadan", "lebaran", "mudik", "idul fitri", "idul adha", "natal", "imlek", "nyepi", "galungan",
            // Government operations/programs
            "operasi ketupat", "operasi lilin", "operasi zebra", "operasi patuh", "vaksinasi",
            // Political events
            "pemilu", "pilkada", "pilpres", "pileg", "kampanye", "debat capres", "pelantikan",
            // Economic events
            "resesi", "inflasi", "kenaikan harga", "subsidi", "bbm", "phk", "umr", "ump"
        ];

        // Organization keywords expanded
        const orgKeywords = [
            // Government institutions
            "kpk", "polda", "polres", "polsek", "dpr", "dprd", "mpr", "kemenkes", "kemendikbud",
            "kemenlu", "kemenhub", "kemenkeu", "bappenas", "bpjs", "bmkg", "bnpb", "bnn", "kpu",
            // Political parties
            "pdip", "golkar", "demokrat", "gerindra", "pks", "pan", "psi", "pkb", "ppp", "hanura", "nasdem",
            // Universities
            "ui", "ugm", "itb", "unpad", "undip", "unair", "uki", "uaj", "upn", "ipb",
            // NGOs and other organizations
            "dompet dhuafa", "act", "mui", "nu", "muhammadiyah", "walhi", "amnesty", "icw", "ylki",
            // Companies
            "pertamina", "pln", "garuda", "telkom", "indosat", "bri", "bni", "mandiri", "bca"
        ];

        // Person keywords for titles about individuals
        const personKeywords = [
            "presiden", "menteri", "gubernur", "bupati", "walikota", "camat", "lurah",
            "direktur", "ketua", "sekjen", "jubir", "juru bicara", "tersangka", "terdakwa",
            "artis", "selebriti", "penyanyi", "aktor", "aktris", "atlet", "pelatih"
        ];

        // Check against each category
        if (eventKeywords.some(keyword => lowerText.includes(keyword))) {
            return "event";
        }

        if (orgKeywords.some(org => lowerText.includes(org))) {
            return "organization";
        }

        if (personKeywords.some(keyword => lowerText.includes(keyword))) {
            return "person";
        }

        // Return original type if no matches
        return type;
    };

    const cleanEntity = (entity) => {
        // Remove punctuation (dot, comma, colon, semicolon)
        return entity.replace(/[.,:;]/g, '').trim();
    };

    const extractEntities = useCallback((newsItems) => {
        setIsLoading(true);

        const entityMap = new Map();
        const allTitles = newsItems.map(item => item.description).join(' ').toLowerCase();

        // First pass: collect all entities
        newsItems.forEach((item) => {
            const text = item.title;
            const doc = nlp(text);

            const people = doc.people().out('array');
            const places = doc.places().out('array');
            const orgs = doc.organizations().out('array');

            const addEntity = (entity, type) => {
                // Clean entity by removing trailing punctuation
                const cleanedEntity = cleanEntity(entity).replace(/[.,-/#!$%^*&;:{}=-_`~()]+$/, '').toLowerCase();
                if (!cleanedEntity ||
                    cleanedEntity.length <= 2 ||
                    cleanedEntity.includes('http') ||
                    cleanedEntity.includes('.com')) {
                    return;
                }

                const globalFrequency = (allTitles.match(new RegExp(`\\b${escapeRegExp(cleanedEntity)}\\b`, 'gi')) || []).length;

                if (!entityMap.has(cleanedEntity)) {
                    entityMap.set(cleanedEntity, {
                        frequency: globalFrequency,
                        type: adjustType(cleanedEntity, type),
                        originalText: entity,
                        wordCount: cleanedEntity.split(' ').length,
                        length: cleanedEntity.length,
                        isCommonTerm: isCommonTerm(cleanedEntity)
                    });
                }
            };

            people.forEach((person) => addEntity(person, 'person'));
            places.forEach((place) => addEntity(place, 'place'));
            orgs.forEach((org) => addEntity(org, 'organization'));
        });

        function escapeRegExp(string) {
            return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        }

        function isCommonTerm(entity) {
            const commonTerms = new Set([
                'yang', 'dengan', 'pada', 'dari', 'untuk', 'dalam', 'dan', 'ada', 'ini', 'itu',
                'oleh', 'akan', 'tidak', 'saat', 'para', 'sita', 'bisa', 'harus', 'sudah', 'juga',
                'tapi', 'atau', 'jika', 'maka', 'karena', 'sekitar', 'meski', 'serta', 'hingga'
            ]);

            return commonTerms.has(entity) ||
                entity.match(/(yang|saat|usai|dengan|tak|bikin|ini|jika|untuk|para|sita)$/i);
        }

        function isPartialWordOnly(entity) {
            const wordBoundaryMatches = (allTitles.match(new RegExp(`\\b${escapeRegExp(entity)}\\b`, 'gi')) || []).length;
            const totalMatches = (allTitles.match(new RegExp(escapeRegExp(entity), 'gi')) || []).length;

            return wordBoundaryMatches < 1 && totalMatches > 1;
        }

        let entitiesArray = Array.from(entityMap.entries())
            .map(([text, props]) => ({
                text,
                ...props,
                isPartialWord: isPartialWordOnly(text)
            }));

        // Stage 1: Strict filtering for high-quality entities
        const strictlyFilteredEntities = entitiesArray.filter(entity =>
            entity.frequency >= 2 &&
            !entity.isCommonTerm &&
            !entity.isPartialWord &&
            entity.length > 3 &&
            entity.wordCount <= 5 // Increased to handle up to 5 words
        );

        // Enhanced name variant detection for 1-5 words
        const personEntities = strictlyFilteredEntities.filter(e => e.type === 'person');
        const nameVariantMap = new Map();

        personEntities.forEach(entity => {
            const words = entity.text.split(' ');
            words.forEach((word, index) => {
                if (word.length > 2) { // Slightly relaxed length check
                    const baseName = words.slice(0, index + 1).join(' ');
                    if (!nameVariantMap.has(baseName)) {
                        nameVariantMap.set(baseName, []);
                    }
                    nameVariantMap.get(baseName).push(entity);
                }
            });
        });

        const toRemove = new Set();

        nameVariantMap.forEach((variants, baseName) => {
            if (variants.length > 1) {
                variants.sort((a, b) => {
                    // Sort by word count (longer first), then frequency
                    if (a.wordCount !== b.wordCount) {
                        return b.wordCount - a.wordCount;
                    }
                    return b.frequency - a.frequency;
                });

                const bestVariant = variants[0];
                let totalFrequency = bestVariant.frequency;

                for (let i = 1; i < variants.length; i++) {
                    // Check if one is a substring of another or very similar
                    if (bestVariant.text.includes(variants[i].text) ||
                        variants[i].text.includes(bestVariant.text) ||
                        levenshteinDistance(bestVariant.text, variants[i].text) < 3) { // Allow for minor typos
                        totalFrequency += variants[i].frequency;
                        toRemove.add(variants[i].text);
                    }
                }

                bestVariant.frequency = totalFrequency;
            }
        });

        // Remove duplicates and variants
        let filteredEntities = strictlyFilteredEntities.filter(entity => !toRemove.has(entity.text));

        // Remove entities that are substrings of others, regardless of word count
        filteredEntities = filteredEntities.filter((entity, index, self) => {
            return !self.some((other, otherIndex) => {
                return index !== otherIndex &&
                    other.text.includes(entity.text) &&
                    entity.text.length < other.text.length &&
                    other.type === entity.type &&
                    other.frequency >= entity.frequency;
            });
        });

        // Sort by frequency then alphabetically
        let finalEntities = filteredEntities
            .sort((a, b) => b.frequency - a.frequency || a.text.localeCompare(b.text));

        // Relaxation stages remain similar but adjusted for new word count limit
        if (finalEntities.length < 10) {
            const relaxedFilterEntities = entitiesArray.filter(entity =>
                !toRemove.has(entity.text) &&
                entity.frequency >= 1 &&
                !entity.isPartialWord &&
                entity.length > 3 &&
                !entity.isCommonTerm &&
                entity.wordCount <= 5
            );

            const existingTexts = new Set(finalEntities.map(e => e.text));
            const additionalEntities = relaxedFilterEntities
                .filter(entity => !existingTexts.has(entity.text))
                .sort((a, b) => b.frequency - a.frequency || a.text.localeCompare(b.text));

            finalEntities = [
                ...finalEntities,
                ...additionalEntities.slice(0, 10 - finalEntities.length)
            ];
        }

        if (finalEntities.length < 10) {
            const minimalFilterEntities = entitiesArray.filter(entity =>
                !toRemove.has(entity.text) &&
                entity.length > 2 &&
                !existingTexts.has(entity.text) &&
                entity.wordCount <= 5
            );

            const existingTexts = new Set(finalEntities.map(e => e.text));
            const emergencyEntities = minimalFilterEntities
                .filter(entity => !existingTexts.has(entity.text))
                .sort((a, b) => b.frequency - a.frequency || a.text.localeCompare(b.text));

            finalEntities = [
                ...finalEntities,
                ...emergencyEntities.slice(0, 10 - finalEntities.length)
            ];
        }

        finalEntities = finalEntities.slice(0, 10);

        const cleanOutput = finalEntities.map(({ text, type, frequency, originalText }) => ({
            text, type, frequency, originalText
        }));

        setEntities(cleanOutput);
        setIsLoading(false);

        return cleanOutput;
    }, []);

    function levenshteinDistance(a, b) {
        const matrix = Array(b.length + 1).fill(null).map(() => Array(a.length + 1).fill(null));

        for (let i = 0; i <= a.length; i++) matrix[0][i] = i;
        for (let j = 0; j <= b.length; j++) matrix[j][0] = j;

        for (let j = 1; j <= b.length; j++) {
            for (let i = 1; i <= a.length; i++) {
                const indicator = a[i - 1] === b[j - 1] ? 0 : 1;
                matrix[j][i] = Math.min(
                    matrix[j][i - 1] + 1, // deletion
                    matrix[j - 1][i] + 1, // insertion
                    matrix[j - 1][i - 1] + indicator // substitution
                );
            }
        }

        return matrix[b.length][a.length];
    }

    useEffect(() => {
        if (news && news.length > 0) {
            extractEntities(news);
        }
    }, [news, extractEntities]);

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
        onSearch(e.target.value);
    };

    const clearSearch = () => {
        setSearchTerm('');
        onSearch('');
    };

    const toggleTheme = () => {
        setIsDarkMode((prev) => {
            const newMode = !prev;
            document.body.classList.toggle('dark-mode', newMode);
            return newMode;
        });
    };

    const handleReload = () => {
        clearSearch();
        setCurrentTime(new Date());
        window.location.reload();
    };

    return (
        <header className={`site-header ${isScrolled ? 'scrolled' : ''}`}>
            <div className="header-container">
                <div
                    className="logo-container"
                    onClick={handleReload}
                    title="Muat Ulang"
                    aria-label="Reload the page"
                    role="button"
                    tabIndex="0"
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            handleReload();
                        }
                    }}
                >
                    <h1>Selidik</h1>
                    <span className="tagline">Agregator Berita Indonesia</span>
                </div>

                <div className={`search-container ${isSearchFocused ? 'focused' : ''}`}>
                    <div className="search-input-wrapper">
                        <FaSearch className="search-icon" />
                        <input
                            type="text"
                            placeholder="Cari berita..."
                            value={searchTerm}
                            onChange={handleSearchChange}
                            onFocus={() => setIsSearchFocused(true)}
                            onBlur={() => setIsSearchFocused(false)}
                        />
                        {searchTerm && (
                            <button className="clear-search" onClick={clearSearch} aria-label="Clear search">
                                <FaTimes />
                            </button>
                        )}
                    </div>
                </div>

                <div className="header-action-buttons">
                    <button
                        className="header-action-button"
                        onClick={() => setIsDisclaimerPopupOpen(true)}
                        title="Info"
                        aria-label="News Info"
                    >
                        <FaInfoCircle />
                        {news.length > 0 && <span>{news.length}</span>}
                    </button>

                    <button
                        className="header-action-button"
                        onClick={toggleTheme}
                        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
                    >
                        {isDarkMode ? <MdLightMode /> : <MdDarkMode />}
                    </button>
                </div>
            </div>

            {/* Trending Ticker (moved from TrendingTicker component) */}
            {!isLoading && entities.length > 0 && (
                <div className="trending-ticker-container">
                    <div className="trending-ticker">
                        {entities.map((entity, index) => (
                            <span
                                key={index}
                                className={`entity-item ${entity.type}`}
                                onClick={() => setSearchTerm(entity.text)}
                            >
                                {entity.text}
                            </span>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
};

export default Header;